import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import DateFnsUtils from "@date-io/date-fns";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import UserDataService from "../../services/UserService";
import InvitationsDataService from "../../services/InvitationsService";
import DevicesDataService from "../../services/DeviceService";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import AppBarBasic from "../layout/AppBar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import DeleteIcon from "@material-ui/icons/Delete";
import { Tooltip } from "@mui/material";
import axios from "axios";
import { API_URL } from "../../utils/setApiUrl";
import CompanyDataService from "../../services/CompanyService";
import DeviceUsersService from "../../services/DeviceUsersService";
import DeviceFloorService from "../../services/DeviceFloorService";

const drawerWidth = 280;

const BlackTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "black",
    fontSize: 13,
    padding: "8px 12px",
    borderRadius: 4,
  },
  arrow: {
    color: "black",
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexGrow: 1,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    background: "linear-gradient(#006987 0%, #003544 100%)",
    border: "none",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    height: "100%",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: "16px",
  },
  invitationsList: {
    overflow: "auto",
    maxHeight: "410px",
    marginTop: theme.spacing(2),
    // Add padding to the right for scrollbar spacing
    paddingRight: "5px",
    // Move scrollbar to account for padding
    marginRight: "-5px",
    position: "relative",
    width: "100%",
    // Add custom scrollbar styling
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  listItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: "72px",
    marginRight: "5px",
    paddingRight: "48px",
    width: "auto",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  deletedListItem: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    opacity: 0.7,
  },

  deletedText: {
    textDecoration: "line-through !important",
    textDecorationColor: theme.palette.text.primary,
    textDecorationThickness: "1px",
  },
  deleteButton: {
    color: theme.palette.error.light,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
  listHeader: {
    display: "flex",
    flexDirection: "column",
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
  datePicker: {
    width: "100%",
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
}));

const InvitePage = () => {
  const classes = useStyles();

  // State management
  const [users, setUsers] = useState([]); // For existing users
  const [isNewUser, setIsNewUser] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // First, modify the form state to combine both functionalities:
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    selectedUser: null,
    selectedRoom: null,
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
  });

  const [companyData, setCompanyData] = useState({
    name: "",
    address: "",
    owner: "",
  });

  // Separate useEffects for independent fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, roomsRes, invitationsRes, companyRes] =
          await Promise.all([
            UserDataService.adminAll(),
            DevicesDataService.adminAllRooms(),
            InvitationsDataService.adminAll({ paranoid: false }),
            CompanyDataService.get(1),
          ]);

        setUsers(usersRes.data);
        setRooms(roomsRes.data);
        setCompanyData(companyRes.data);

        const sortedInvitations = [...invitationsRes.data].sort((a, b) => {
          if (a.deletedAt && !b.deletedAt) return 1;
          if (!a.deletedAt && b.deletedAt) return -1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setInvitations(sortedInvitations);
      } catch (error) {
        console.error("Error fetching data:", error);
        showSnackbar("Error loading data", "error");
      }
    };

    fetchData();
  }, []);

  // Helper function for snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    const client = axios.create({
      baseURL: API_URL,
    });

    e.preventDefault();

    if (!formData.selectedRoom || !formData.startDate || !formData.endDate) {
      showSnackbar("Моля попълнете всички полета", "error");
      return;
    }

    if (isNewUser && (!formData.name || !formData.email)) {
      showSnackbar("Моля попълнете всички полета за нов потребител", "error");
      return;
    }

    if (!isNewUser && !formData.selectedUser) {
      showSnackbar("Моля изберете потребител", "error");
      return;
    }

    try {
      let userData;

      if (isNewUser) {
        const guestId = uuidv4().replace(/-/g, "");
        userData = {
          name: formData.name,
          email: formData.email,
          guest_id: guestId, // Add this explicitly
          start_date: dayjs(formData.startDate).format("YYYY-MM-DD HH:mm"),
          end_date: dayjs(formData.endDate).format("YYYY-MM-DD HH:mm"),
          loc_number: 1,
          room_name: formData.selectedRoom.name,
          loc_name: companyData.name,
          sub_loc_name: "Гости",
          status: 3,
        };
        const userResponse = await UserDataService.create(userData);
        userData = { ...userResponse.data, guest_id: guestId }; // Ensure guest_id is preserved
      } else {
        userData = formData.selectedUser;
      }

      const invitationData = {
        inviter_name: companyData.name,
        inviter_email: "no-reply@biodit.com",
        inviter_company: companyData.name,
        invited_name: isNewUser ? formData.name : userData.name,
        invited_email: isNewUser ? formData.email : userData.email,
        invited_uuid: userData.guest_id,
        reservation_start: dayjs(formData.startDate).format("YYYY-MM-DD HH:mm"),
        reservation_end: dayjs(formData.endDate).format("YYYY-MM-DD HH:mm"),
        meeting_title: "Достъп до стая",
        meeting_purpose: "Достъп до стая",
        meeting_room: formData.selectedRoom.name,
        meeting_location: companyData.address,
      };

      const invitationResponse = await InvitationsDataService.create(
        invitationData
      );
      setInvitations((prev) => [invitationResponse.data, ...prev]);

      // Additional axios POST request to /invite
      await client.post("/invite", invitationData);

      // Get the floor information for the selected room
      const floorInfo = await DeviceFloorService.getFloorsForDevice(formData.selectedRoom.id);
      const floor = floorInfo.data[0] && floorInfo.data[0].Floor ? floorInfo.data[0].Floor.level : 0;

      // Get free slot for the device
      const slotResponse = await client.post("/freeSlot", {
        device: formData.selectedRoom.id,
      });
      const slot = slotResponse.data.message;

      // Create device user record
      await DeviceUsersService.create({
        device_id: formData.selectedRoom.id,
        user_id: isNewUser ? userData.sap : userData.sap,
        slot: slot,
        template_id: null,
      });

      // Send MQTT insert command
      await client.post("/insert", {
        device: formData.selectedRoom.mac,
        slot: slot,
        user: userData.sap,
        pin: null,
        template: null,
        card: null,
        qr_code: userData.guest_id,
        floor: floor,
      });

      // Get all elevator devices
      const elevatorDevices =
        await DevicesDataService.adminAllElevatorDevices();
      const elevators = elevatorDevices.data;

      // For each elevator device
      for (const elevator of elevators) {
        // Get free slot
        const elevatorSlotResponse = await client.post("/freeSlot", {
          device: elevator.id,
        });
        const elevatorSlot = elevatorSlotResponse.data.message;

        // Create device user record for elevator
        await DeviceUsersService.create({
          device_id: elevator.id,
          user_id: isNewUser ? userData.sap : userData.sap,
          slot: elevatorSlot,
          template_id: null,
        });

        // Send MQTT insert command for elevator
        await client.post("/insert", {
          device: elevator.mac,
          slot: elevatorSlot,
          user: userData.sap,
          pin: null,
          template: null,
          card: userData.card,
          qr_code: userData.guest_id,
          floor: floor,
        });
      }

      setFormData({
        name: "",
        email: "",
        selectedUser: null,
        selectedRoom: null,
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
      });

      showSnackbar("QR кодът е генериран успешно", "success");
    } catch (error) {
      console.error("Error:", error);
      showSnackbar("Грешка при генериране на QR код", "error");
    }
  };

  // Add the delete handler function in your component:
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    invitationId: null,
  });

  const handleDeleteClick = (invitationId) => {
    setDeleteDialog({
      open: true,
      invitationId,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      invitationId: null,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      // Get the invitation details before deleting
      const invitation = invitations.find(inv => inv.id === deleteDialog.invitationId);
      console.log('Found invitation:', invitation);
      
      // Delete the invitation
      await InvitationsDataService.remove(deleteDialog.invitationId);
      console.log('Invitation deleted');

      // Get user details using the QR code
      const userResponse = await UserDataService.getByQRCode(invitation.invited_uuid);
      console.log('User response:', userResponse);

      if (userResponse.data) {
        const userSap = userResponse.data.sap;
        console.log('User SAP:', userSap);

        const client = axios.create({
          baseURL: API_URL,
        });

        // Remove user from all devices in the database
        console.log('Removing from DeviceUsers...');
        await DeviceUsersService.remove(userSap);
        console.log('Removed from DeviceUsers');

        // Send MQTT command to remove user from all physical devices
        console.log('Sending removeUserEverywhere command...');
        await client.post('/removeUserEverywhere', {
          user: userSap.toString()
        });
        console.log('removeUserEverywhere command sent');

        // Delete the user
        await UserDataService.remove(userSap);
        console.log('User deleted');
      } else {
        console.log('No user data found for QR code:', invitation.invited_uuid);
      }

      // Fetch updated invitations list
      const invitationsRes = await InvitationsDataService.adminAll({ paranoid: false });
      const sortedInvitations = [...invitationsRes.data].sort((a, b) => {
        if (a.deletedAt && !b.deletedAt) return 1;
        if (!a.deletedAt && b.deletedAt) return -1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setInvitations(sortedInvitations);
      
      showSnackbar("QR кодът е изтрит успешно", "success");
    } catch (error) {
      console.error("Error deleting invitation:", error);
      console.error("Error details:", error.response ? error.response.data : error.message);
      showSnackbar("Грешка при изтриване на QR код", "error");
    }
    handleDeleteCancel();
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBarBasic />
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {/* Header Section */}
        <Container maxWidth="lg" className={classes.container}>
          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
          <Grid container className={classes.header}>
            <Grid item xs={12}>
              <Typography variant="h4" component="h1" align="center">
                Генериране на QR код за достъп
              </Typography>
              <Typography variant="body1" align="center" color="textSecondary">
                Изберете потребител и период за достъп до избраната стая
              </Typography>
            </Grid>
          </Grid>

          {/* Main Content */}
          <Grid container spacing={2}>
            {/* Form Section */}
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography variant="h6" gutterBottom>
                  Издаване на QR код
                </Typography>
                <Divider />
                <form onSubmit={handleSubmit} className={classes.form}>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <Button
                      variant={isNewUser ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => setIsNewUser(true)}
                    >
                      Нов потребител
                    </Button>
                    <Button
                      variant={!isNewUser ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => setIsNewUser(false)}
                    >
                      Съществуващ потребител
                    </Button>
                  </div>

                  {isNewUser ? (
                    <>
                      <TextField
                        label="Име"
                        variant="outlined"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        size="small"
                      />
                      <TextField
                        label="Имейл"
                        variant="outlined"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        size="small"
                      />
                    </>
                  ) : (
                    <Autocomplete
                      options={users}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.email})`
                      }
                      value={formData.selectedUser}
                      onChange={(_, newValue) =>
                        setFormData({ ...formData, selectedUser: newValue })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Изберете потребител"
                          variant="outlined"
                          required
                          size="small"
                        />
                      )}
                    />
                  )}

                  <Autocomplete
                    options={rooms}
                    getOptionLabel={(option) => `${option.name}`}
                    value={formData.selectedRoom}
                    size="small"
                    onChange={(_, newValue) =>
                      setFormData({ ...formData, selectedRoom: newValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Изберете стая"
                        variant="outlined"
                        required
                      />
                    )}
                  />

                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDateTimePicker
                      variant="inline"
                      inputVariant="outlined"
                      label="Начало на резервация"
                      value={formData.startDate}
                      onChange={(date) =>
                        setFormData({ ...formData, startDate: date })
                      }
                      format="dd/MM/yyyy HH:mm"
                      ampm={false}
                      className={classes.datePicker}
                      required
                      size="small"
                    />

                    <KeyboardDateTimePicker
                      variant="inline"
                      inputVariant="outlined"
                      label="Край на резервация"
                      value={formData.endDate}
                      onChange={(date) =>
                        setFormData({ ...formData, endDate: date })
                      }
                      format="dd/MM/yyyy HH:mm"
                      ampm={false}
                      className={classes.datePicker}
                      required
                      size="small"
                      minDate={formData.startDate}
                    />
                  </MuiPickersUtilsProvider>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    className={classes.submitButton}
                  >
                    Генериране на QR код
                  </Button>
                </form>
              </Paper>
            </Grid>

            {/* Invitations List Section */}
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography variant="h6" gutterBottom>
                  Последно генерирани QR кодове
                </Typography>
                <Divider />
                <List className={classes.invitationsList} disablePadding>
                  {invitations.map((invitation) => (
                    <ListItem
                      key={invitation.id}
                      className={`${classes.listItem} ${
                        invitation.deletedAt ? classes.deletedListItem : ""
                      }`}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            className={
                              invitation.deletedAt ? classes.deletedText : ""
                            }
                            style={{ display: "inline-block", width: "100%" }}
                          >
                            {invitation.invited_name +
                              ` - Стая ${invitation.meeting_room}`}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                            className={
                              invitation.deletedAt ? classes.deletedText : ""
                            }
                            style={{ display: "inline-block", width: "100%" }}
                          >
                            {dayjs(invitation.reservation_start).format(
                              "DD/MM/YYYY HH:mm"
                            )}{" "}
                            -{" "}
                            {dayjs(invitation.reservation_end).format(
                              "DD/MM/YYYY HH:mm"
                            )}
                          </Typography>
                        }
                      />
                      {!invitation.deletedAt && (
                        <ListItemSecondaryAction>
                          <BlackTooltip
                            title="Прекратяване на QR"
                            arrow
                            placement="left"
                          >
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              className={classes.deleteButton}
                              onClick={() => handleDeleteClick(invitation.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </BlackTooltip>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Paper>
              {/* Add Dialog */}
              <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
                <DialogTitle style={{ paddingBottom: 0 }}>
                  Потвърждение за изтриване
                </DialogTitle>
                <DialogContent style={{ paddingTop: "8px" }}>
                  Сигурни ли сте, че искате да изтриете този достъп?
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleDeleteConfirm}
                    color="primary"
                    variant="contained"
                  >
                    Да
                  </Button>
                  <Button onClick={handleDeleteCancel} color="primary">
                    Не
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Grid>
        </Container>
      </main>
    </div>
  );
};

export default InvitePage;
